import { useEffect } from "react";
import { useLocation, useParams } from "react-router";
import { FILESTREAM_ROUTE, PAGE_NOT_FOUND_ROUTE } from "Constants";

// Full URL of the group-scoped page the user was last on, so the header can
// offer a "Back to <group>" return once they detour into an org-level page.
const STORE_LAST_GROUP_PATH = "lastGroupPath";

export type LastGroupLocation = {
  groupId: string;
  path: string;
};

const readLastGroupLocation = (): LastGroupLocation | null => {
  const raw = localStorage.getItem(STORE_LAST_GROUP_PATH);

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<LastGroupLocation>;

    return parsed.groupId && parsed.path
      ? { groupId: parsed.groupId, path: parsed.path }
      : null;
  } catch {
    return null;
  }
};

/**
 * Remembers the full URL of the group-scoped page the user was last on.
 *
 * While the user is inside a group (the URL carries a `:groupId`) it records the
 * exact page they are viewing. Once they detour into an organization-level page
 * — where the group context, and therefore the header group select, is gone — it
 * returns that remembered location so the header can offer a "Back to <group>"
 * affordance that drops them exactly where they left off.
 *
 * Returns `null` while a group is active (nothing to go "back" to).
 */
const useLastGroupLocation = (): LastGroupLocation | null => {
  const { pathname, search } = useLocation();
  const { groupId } = useParams();

  useEffect(() => {
    if (groupId) {
      localStorage.setItem(
        STORE_LAST_GROUP_PATH,
        JSON.stringify({ groupId, path: `${pathname}${search}` }),
      );
    }
  }, [groupId, pathname, search]);

  if (groupId) return null;

  const lastGroupLocation = readLastGroupLocation();

  return lastGroupLocation && pathname === `/${PAGE_NOT_FOUND_ROUTE}`
    ? {
        groupId: lastGroupLocation.groupId,
        path: `/${FILESTREAM_ROUTE}/group/${lastGroupLocation.groupId}`,
      }
    : lastGroupLocation;
};

export default useLastGroupLocation;
